import React from 'react';
import PropTypes from 'prop-types';
import { Query } from '@apollo/client/react/components';
import { FormattedMessage } from 'react-intl';
import { NavLink } from 'react-router-dom';
import classnames from 'classnames';
import Styled from 'styled';

import SVGIcon from 'lib/images/svg-icon/svg-icon';

import { ViewerConsumer } from 'four-nets/user/viewer/viewer';
import { Site } from 'four-nets/site';
import { HideForVendor } from 'four-nets/vendor/permissions';
import arrow from 'four-nets/icons/svg/icon_arrow-down.svg';
import user from 'four-nets/icons/svg/icon_user.svg';
import feed from 'four-nets/icons/svg/icon_feed.svg';
import users from 'four-nets/icons/svg/icon_users.svg';
import professionals from 'four-nets/icons/svg/icon_proffesional.svg';
import comment from 'four-nets/icons/svg/icon_comment.svg';
import like from 'four-nets/icons/svg/icon_like.svg';
import friendship from 'four-nets/icons/svg/icon_friendship.svg';
import submenu from 'four-nets/icons/svg/icon_menu-sub.svg';
import subsection from 'four-nets/icons/svg/icon_sub-section.svg';
import gallery from 'four-nets/icons/svg/icon_gallery.svg';
import heart from 'four-nets/icons/svg/icon_heart.svg';
import staroutlined from 'four-nets/icons/svg/icon_star-outlined.svg';
import moderator from 'four-nets/icons/svg/icon_moderator.svg';
import articles from 'four-nets/icons/svg/icon_articles.svg';

import friendsLastVisitCountQuery, {
    FriendsLastVisitCountQuery,
} from 'four-nets/photoblog/latest-albums/friends-last-visit-count.graphql';
import query, { LeftMenuQuery } from './left-menu.graphql';

import arrowStyles from './arrow.scss';
import defaultStyles from './left-menu.scss';

interface Props {
    styles: typeof defaultStyles;
}

interface State {
    open: boolean;
}

class LeftMenu extends React.PureComponent<Props> {
    static contextTypes = {
        site: PropTypes.instanceOf(Site),
    };
    state = { open: false };

    toggleOpen = () => {
        this.setState((prevState: State) => ({ open: !prevState.open }));
    };

    render() {
        const { styles, children } = this.props;
        const { site } = this.context;

        return (
            <div className={styles.wrapper}>
                <div className={classnames(styles.innerContainer, { [styles.open]: this.state.open })}>
                    <button className={styles.openButton} onClick={this.toggleOpen} aria-label='Menu arrow icon' />
                    <SVGIcon
                        style={{ image: arrowStyles.image }}
                        src={arrow}
                        className={this.state.open ? arrowStyles.upside : undefined}
                    />
                    <div className={classnames(styles.sectionTitle, styles.hide)}>
                        <FormattedMessage id='PB_MENU_FROM_PHOTOBLOGS' />
                    </div>
                    <NavLink
                        exact={true}
                        to='/blogs/'
                        className={classnames(styles.navLink, { [styles.menuOpen]: this.state.open })}
                        activeClassName={styles.active}
                    >
                        <SVGIcon style={{ image: styles.navIcon }} src={feed} />
                        <FormattedMessage id='PHOTOBLOGS_ALL' />
                    </NavLink>
                    <NavLink
                        exact={true}
                        to='/blogs/my-selection/'
                        className={classnames(styles.navLink, { [styles.menuOpen]: this.state.open })}
                        activeClassName={styles.active}
                    >
                        <SVGIcon style={{ image: styles.navIcon }} src={users} />
                        <FormattedMessage id='MY_SELECTION' />
                    </NavLink>
                    <NavLink
                        to='/blogs/friends/'
                        className={classnames(styles.navLink, styles.elipsisLink, {
                            [styles.menuOpen]: this.state.open,
                        })}
                        activeClassName={styles.active}
                    >
                        <SVGIcon style={{ image: styles.navIcon }} src={friendship} />
                        <Query<FriendsLastVisitCountQuery> query={friendsLastVisitCountQuery} fetchPolicy='cache-first'>
                            {({ data }) => (
                                <>
                                    <FormattedMessage id='PB_MENU_FRIENDS_AND_FAVORITES'>
                                        {msg => (
                                            <span
                                                className={styles.elipsisName}
                                                style={{ maxWidth: data?.friendsLastVisitCount ? '80%' : '100%' }}
                                            >
                                                {msg}
                                            </span>
                                        )}
                                    </FormattedMessage>
                                    {data?.friendsLastVisitCount ? (
                                        <span className={styles.count}>{data.friendsLastVisitCount}</span>
                                    ) : null}
                                </>
                            )}
                        </Query>
                    </NavLink>
                    {!site.isGarden ? (
                        <>
                            <NavLink
                                to='/blogs/professionals/'
                                className={classnames(styles.navLink, { [styles.menuOpen]: this.state.open })}
                                activeClassName={styles.active}
                            >
                                <SVGIcon style={{ image: styles.navIcon }} src={professionals} />
                                <FormattedMessage id='PB_MENU_VENDORS' />
                            </NavLink>
                            <NavLink
                                to={`/blog/${site.prArticlesUsername}/`}
                                className={classnames(styles.navLink, { [styles.menuOpen]: this.state.open })}
                                activeClassName={styles.active}
                            >
                                <SVGIcon style={{ image: styles.navIcon }} src={articles} />
                                <FormattedMessage id='PR_ARTICLES_SHORT' />
                            </NavLink>
                        </>
                    ) : null}
                    <NavLink
                        to='/blogs/popular/'
                        className={classnames(styles.navLink, { [styles.menuOpen]: this.state.open })}
                        activeClassName={styles.active}
                    >
                        <SVGIcon style={{ image: styles.navIcon }} src={staroutlined} />
                        <FormattedMessage id='POPULAR_IMAGES' />
                    </NavLink>
                    {!site.isGarden ? (
                        <NavLink
                            to='/inspirations/'
                            className={classnames(styles.navLink, { [styles.menuOpen]: this.state.open })}
                            activeClassName={styles.active}
                        >
                            <SVGIcon style={{ image: styles.navIcon }} src={moderator} />
                            <FormattedMessage id='TAGGING_TITLE_LIVING' />
                        </NavLink>
                    ) : null}
                    <div className={classnames(styles.sectionTitle, styles.bottomContainer, styles.hide)}>
                        <FormattedMessage id='COLLECTIONS' />
                    </div>
                    <NavLink
                        exact={true}
                        to='/blogs/collections/'
                        className={classnames(styles.navLink, { [styles.menuOpen]: this.state.open })}
                        activeClassName={styles.active}
                    >
                        <SVGIcon style={{ image: styles.navIcon }} src={gallery} />
                        <FormattedMessage id='PB_MENU_COLLECTION_LIST' />
                    </NavLink>
                    <NavLink
                        to='/blogs/collections/mine/'
                        className={classnames(styles.navLink, { [styles.menuOpen]: this.state.open })}
                        activeClassName={styles.active}
                    >
                        <SVGIcon style={{ image: styles.navIcon }} src={user} />
                        <FormattedMessage id='PB_MENU_COLLECTION_MINE' />
                    </NavLink>
                    <NavLink
                        to='/blogs/collections/favourite/'
                        className={classnames(styles.navLink, { [styles.menuOpen]: this.state.open })}
                        activeClassName={styles.active}
                    >
                        <SVGIcon style={{ image: styles.navIcon }} src={heart} />
                        <FormattedMessage id='PB_MENU_COLLECTION_FAVORITE' />
                    </NavLink>
                    {!site.isGarden ? (
                        <HideForVendor>
                            <div className={classnames(styles.sectionTitle, styles.hide, styles.marginTop)}>
                                <FormattedMessage id='GROUPS' />
                            </div>
                            <NavLink
                                to='/list-of-groups/'
                                exact={true}
                                className={classnames(styles.navLink, { [styles.menuOpen]: this.state.open })}
                                activeClassName={styles.active}
                            >
                                <SVGIcon style={{ image: styles.navIcon }} src={submenu} />
                                <FormattedMessage id='GROUP_LIST' />
                            </NavLink>
                            <NavLink
                                to='/list-of-groups/mine/'
                                className={classnames(styles.navLink, styles.mobileOnly, {
                                    [styles.menuOpen]: this.state.open,
                                })}
                                activeClassName={styles.active}
                            >
                                <SVGIcon style={{ image: styles.navIcon }} src={user} />
                                <FormattedMessage id='GROUPS_MY_GROUPS' />
                            </NavLink>
                        </HideForVendor>
                    ) : null}
                    {children}
                    <ViewerConsumer>
                        {({ viewer }) =>
                            viewer && !site.isGarden ? (
                                <Query<LeftMenuQuery> query={query} fetchPolicy='cache-first'>
                                    {({ data }) =>
                                        data?.myGroups?.edges
                                            ? data.myGroups.edges.map(edge =>
                                                  edge?.node ? (
                                                      <NavLink
                                                          key={edge.node.id}
                                                          to={edge.node.href}
                                                          className={classnames(styles.navLink, styles.elipsisLink)}
                                                          activeClassName={styles.active}
                                                      >
                                                          <SVGIcon style={{ image: styles.navIcon }} src={subsection} />
                                                          <span className={styles.elipsisName}>{edge.node.name}</span>
                                                          {edge.node.viewerUnreadCount ? (
                                                              <span className={styles.count}>
                                                                  {edge.node.viewerUnreadCount}
                                                              </span>
                                                          ) : null}
                                                      </NavLink>
                                                  ) : null
                                              )
                                            : null
                                    }
                                </Query>
                            ) : null
                        }
                    </ViewerConsumer>
                    <div className={classnames(styles.sectionTitle, styles.bottomContainer, styles.hide)}>
                        <FormattedMessage id='MY_ACTIVITY' />
                    </div>
                    <NavLink
                        to='/blogs/my-likes/'
                        className={classnames(styles.navLink, { [styles.menuOpen]: this.state.open })}
                        activeClassName={styles.active}
                    >
                        <SVGIcon style={{ image: styles.navIcon }} src={like} />
                        <FormattedMessage id='PB_MENU_MY_LIKES' />
                    </NavLink>
                    <ViewerConsumer>
                        {({ viewer }) => (
                            <NavLink
                                to='/blogs/my-comments/'
                                className={classnames(styles.navLink, { [styles.menuOpen]: this.state.open })}
                                activeClassName={styles.active}
                            >
                                <SVGIcon style={{ image: styles.navIcon }} src={comment} />
                                <FormattedMessage
                                    id='PB_MENU_MY_COMMENTS'
                                    values={{ gender: viewer && viewer.sex === 1 ? 'f' : 'm' }}
                                />
                            </NavLink>
                        )}
                    </ViewerConsumer>
                </div>
                <div className={styles.ad}></div>
            </div>
        );
    }
}

export default Styled(defaultStyles)(LeftMenu);
